import { useEffect, useState } from 'react';
import { api, type Deal, type DealItem } from '../api';

/**
 * What's in the photos — including while we're still working it out.
 *
 * The state is rendered rather than the items, because those are different
 * questions. A listing whose analysis has not finished and a listing with
 * nothing worth taking both have zero items, and showing an empty section for
 * both tells the user the second thing when the first is true. Somebody
 * deciding whether to walk four blocks needs to know which.
 *
 * So this polls while the server says `pending`, and says so on screen. The
 * polling is bounded: after a couple of minutes something has gone wrong that
 * refreshing will not fix, and hammering the API forever is worse than showing
 * a stale state the user can reload out of.
 */

const POLL_MS = 4000;
const MAX_POLLS = 30;

export function VisionSection({
  deal,
  onUpdate,
}: {
  deal: Deal;
  onUpdate: (deal: Deal) => void;
}) {
  const [polls, setPolls] = useState(0);
  const vision = deal.vision;
  const pending = vision?.pending ?? false;

  useEffect(() => {
    if (!pending || polls >= MAX_POLLS) return;
    const timer = setTimeout(() => {
      api
        .deal(deal.id)
        .then((r) => {
          setPolls((n) => n + 1);
          onUpdate(r.deal);
        })
        // A failed poll is not worth surfacing: the next one may work, and the
        // section already says what state it is in.
        .catch(() => setPolls((n) => n + 1));
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [deal.id, pending, polls, onUpdate]);

  // 'none' means nobody ever asked for this deal to be analysed — an ordinary
  // online retail deal. Rendering a section about photographs would be noise.
  if (!vision || vision.state === 'none') return null;

  if (pending) {
    return (
      <section className="vision vision-pending">
        <h3>
          Looking through the photos<span className="vision-dots" aria-hidden="true" />
        </h3>
        <p>
          {vision.state === 'running'
            ? 'Identifying what’s in this listing — usually about 20 seconds.'
            : 'Queued for analysis.'}
        </p>
        {polls >= MAX_POLLS && (
          <p className="vision-note">
            Still going. Reopen this deal to check again.
          </p>
        )}
      </section>
    );
  }

  if (vision.state === 'failed') {
    return (
      <section className="vision">
        <h3>Photos</h3>
        <p className="vision-note">Couldn’t analyse the photos for this one.</p>
      </section>
    );
  }

  // 'skipped' means there were no usable photographs — worth saying plainly
  // rather than leaving a gap where a section should be.
  if (vision.state === 'skipped') {
    return (
      <section className="vision">
        <h3>Photos</h3>
        <p className="vision-note">No photos to look at.</p>
      </section>
    );
  }

  if (!vision.items.length) {
    return (
      <section className="vision">
        <h3>Photos</h3>
        <p className="vision-note">Nothing identifiable in the photos.</p>
      </section>
    );
  }

  return (
    <section className="vision">
      <h3>
        Spotted in the photos <span>{vision.items.length}</span>
        {vision.estValue.display && (
          <em title="Rough secondhand value of everything identified">
            ~{vision.estValue.display}
          </em>
        )}
      </h3>

      <ul className="vision-items">
        {vision.items.map((item) => (
          <VisionItemRow key={item.id} item={item} />
        ))}
      </ul>

      <p className="vision-note">
        Identified automatically from the listing’s photos
        {vision.model ? ` by ${vision.model}` : ''}. Treat it as a hint, not an
        inventory.
      </p>
    </section>
  );
}

function VisionItemRow({ item }: { item: DealItem }) {
  return (
    <li className="vision-item">
      <div className="vision-item-main">
        <strong>{item.brand ? `${item.brand} ${item.name}` : item.name}</strong>
        {/* Model numbers come from OCR rather than the vision model, so unlike
            everything else in this list they are read rather than inferred —
            worth marking, because it is the one field somebody will paste into
            a parts lookup. */}
        {item.modelNumber && <code title="Read from the label">{item.modelNumber}</code>}
      </div>

      {item.resale ? (
        <div className="vision-item-value">
          <strong>{item.resale.display}</strong>
          {/* The count is the honest part. A band over 6 listings and a band
              over 300 deserve very different trust, and hiding it would let
              both look equally solid. */}
          <small>
            {item.resale.count ? `${item.resale.count} listings` : 'estimated'}
          </small>
        </div>
      ) : (
        <div className="vision-item-value">
          <small>no comps</small>
        </div>
      )}
    </li>
  );
}
